import { useCallback, useEffect, useState, useMemo, Fragment } from "react";
import { api } from "../api";
import { Save, RotateCcw, ShieldCheck, Lock } from "lucide-react";

const ROLE_STYLE = {
  admin: { bg: "#fee2e2", fg: "#b91c1c", border: "#fecaca" },
  manager: { bg: "#fef3c7", fg: "#b45309", border: "#fde68a" },
  viewer: { bg: "#d1fae5", fg: "#047857", border: "#a7f3d0" },
};

function groupBy(arr, key) {
  return arr.reduce((acc, x) => {
    (acc[x[key]] = acc[x[key]] || []).push(x);
    return acc;
  }, {});
}

export default function Entitlements() {
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/entitlements");
    setData(data);
    // Convert cap lists to Set for O(1) toggle
    const mx = {};
    for (const role of data.roles) mx[role] = new Set(data.matrix[role] || []);
    setMatrix(mx);
    setDirty(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    if (!data) return {};
    return groupBy(data.capabilities, "group");
  }, [data]);

  const protectedSet = useMemo(() => new Set(data?.protectedAdminCaps || []), [data]);

  function toggle(role, code) {
    // Lock protected caps on admin — cannot be unticked
    if (role === "admin" && protectedSet.has(code)) return;
    setMatrix((prev) => {
      const next = { ...prev, [role]: new Set(prev[role]) };
      if (next[role].has(code)) next[role].delete(code);
      else next[role].add(code);
      return next;
    });
    setDirty(true);
    setMsg("");
  }

  async function save() {
    setBusy(true); setMsg("");
    const payload = { matrix: {} };
    for (const role of Object.keys(matrix)) payload.matrix[role] = Array.from(matrix[role]);
    try {
      await api.put("/admin/entitlements", payload);
      setMsg("✅ Saved. Changes apply immediately on next request.");
      setDirty(false);
      await load();
    } catch (e) {
      setMsg(`❌ ${e?.response?.data?.detail || "Save failed"}`);
    } finally {
      setBusy(false);
    }
  }

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div data-testid="entitlements-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: "#1a3666" }}>
            <ShieldCheck size={22} style={{ color: "#00a3a3" }} />
            Entitlements
            <span className="text-xs font-normal bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Admin</span>
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {data.capabilities.length} capabilities · {data.roles.length} roles · Changes apply immediately after save
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-amber-600 text-sm font-medium">● unsaved changes</span>}
          <button onClick={load} disabled={busy || !dirty} data-testid="ent-reset"
                  className="px-3 py-1.5 text-sm rounded-md border border-slate-300 disabled:opacity-50 flex items-center gap-1">
            <RotateCcw size={14} /> Reset
          </button>
          <button onClick={save} disabled={busy || !dirty} data-testid="ent-save"
                  className="px-4 py-1.5 text-sm rounded-md text-white font-semibold disabled:opacity-50 flex items-center gap-1"
                  style={{ background: "#1a3666" }}>
            <Save size={14} /> {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
      {msg && <div className="mb-3 text-sm" data-testid="ent-msg">{msg}</div>}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
            <tr>
              <th className="px-3 py-2 text-left w-[280px]">Capability</th>
              {data.roles.map((r) => {
                const s = ROLE_STYLE[r] || { bg: "#e2e8f0", fg: "#334155" };
                return (
                  <th key={r} className="px-3 py-2 text-center">
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold"
                          style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>
                      {r}
                    </span>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {matrix[r]?.size ?? 0}/{data.capabilities.length}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Object.entries(groups).map(([groupName, caps]) => (
              <Fragment key={`g-${groupName}`}>
                <tr className="bg-slate-100">
                  <td colSpan={data.roles.length + 1}
                      className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    {groupName}
                  </td>
                </tr>
                {caps.map((c) => (
                  <tr key={c.code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cap-row-${c.code}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{c.label}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{c.code}</div>
                    </td>
                    {data.roles.map((role) => {
                      const checked = matrix[role]?.has(c.code) ?? false;
                      const isLocked = role === "admin" && protectedSet.has(c.code);
                      return (
                        <td key={role} className="px-3 py-2 text-center">
                          <label className={`inline-flex items-center justify-center ${isLocked ? "cursor-not-allowed" : "cursor-pointer"}`}
                                 title={isLocked ? "Locked — admin must retain this" : `Toggle ${c.code} for ${role}`}>
                            <input type="checkbox" checked={checked} disabled={isLocked}
                                   onChange={() => toggle(role, c.code)}
                                   data-testid={`cap-${role}-${c.code}`}
                                   className="w-4 h-4 accent-teal-600" />
                            {isLocked && <Lock size={12} className="ml-1 text-slate-400" />}
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-slate-500">
        💡 <strong>Manage Users</strong> and <strong>Manage Entitlements</strong> are locked ON for <strong>admin</strong>
        to prevent anyone from accidentally locking themselves out of the system.
      </p>
    </div>
  );
}
